import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './styles.scss';
import { InputLabel } from '@konova/ui/input-label';
import { useUniqueId } from '@konova/ui/use-unique-id';
import React, { useState } from 'react';
import Form from 'react-bootstrap/Form';
import ValidationMessage from 'src/components/ValidationMessage';
import { IValidationOptional } from '../../../types';

interface IStyledInput {
  meta: any;
  field: any;
  form: any;
  placeholder?: string;
  type?: string;
  label?: string;
  as?: string;
  rows?: number;
  version?: string;
  icon?: any;
  options?: { name: string; key: string | number; value: any }[];
  onChange?: (val: string) => void;
  readonly?: boolean;
  showValidationMessage?: boolean;
  onFocus?: () => void;
  customClass?: string;
  maxPswLength?: number;
  fieldName?: string;
  disabled?: boolean;
  optionalParams?: IValidationOptional;
  required?: boolean;
}

const StyledInput = ({
  meta,
  field,
  form,
  placeholder,
  type = 'text',
  label,
  as,
  rows,
  version,
  icon,
  options = [],
  onChange,
  readonly,
  showValidationMessage,
  onFocus,
  customClass,
  maxPswLength,
  fieldName,
  disabled,
  optionalParams,
  required,
  ...props
}: IStyledInput) => {
  const { name, value } = field;
  const { setFieldValue, submitCount } = form;
  const { touched, error } = meta;
  const uid = useUniqueId('form-');

  const [hidePassword, toggleHidePassword] = useState<boolean>(true);

  const renderPswIcon = () => {
    let iconNames: any = [];
    if (hidePassword) iconNames = ['fas', 'eye-slash'];
    if (!hidePassword) iconNames = ['fas', 'eye'];
    return (
      <FontAwesomeIcon
        onClick={e => {
          toggleHidePassword(!hidePassword);
          return false;
        }}
        className={`${
          !hidePassword ? 'visible-psw-icon' : 'hide-psw-icon'
        } psw-visibility`}
        icon={iconNames}
      />
    );
  };

  const renderControlElement = () => {
    if (as === 'textarea')
      return (
        <div className="input-wrapper">
          <textarea
            id={uid}
            value={value || ''}
            aria-invalid={touched && !!error}
            rows={rows}
            {...(!label ? { 'aria-label': placeholder } : {})}
            placeholder={placeholder}
            onChange={e => setFieldValue(name, e.target.value)}
            className={`input textarea ${customClass} ${
              submitCount > 0 && error ? 'err-input' : ''
            } ${version === 'highlighted' ? 'highlighted' : ''}`}
            readOnly={readonly}
          />
        </div>
      );
    if (as === 'select')
      return (
        <div className="input-wrapper">
          <Form.Select
            id={uid}
            className={`input ${error ? 'err-input' : ''} ${
              version === 'highlighted' ? 'highlighted' : ''
            }`}
            {...(!label ? { 'aria-label': placeholder } : {})}
            aria-invalid={touched && !!error}
            value={field.value || ''}
            onChange={e =>
              onChange? onChange((e.target as unknown as HTMLSelectElement).value): setFieldValue(name, (e.target as unknown as HTMLSelectElement).value)
            }
          >
            {options.map(option => (
              <option key={option.key} value={option.value}>
                {option.name}
              </option>
            ))}
          </Form.Select>
        </div>
      );
    if (as === 'color')
      return (
        <div className="input-wrapper color-wrapper">
          <input
            aria-invalid={touched && !!error}
            type="color"
            value={value || ''}
            onChange={e => setFieldValue(name, e.target.value)}
          />
          <input
            id={uid}
            type={type}
            {...(!label ? { 'aria-label': placeholder } : {})}
            aria-invalid={touched && !!error}
            value={value || ''}
            placeholder={placeholder}
            onChange={e => setFieldValue(name, e.target.value)}
            className={`input ${submitCount > 0 && error ? 'err-input' : ''} ${
              version === 'highlighted' ? 'highlighted' : ''
            } ${icon ? ' input-group-with-icon' : ''}`}
          />
        </div>
      );
    if (as === 'url')
      return (
        <div className="input-wrapper">
          <div className="web-link">{'https://'}</div>
          <input
            id={uid}
            type="text"
            {...(!label ? { 'aria-label': placeholder } : {})}
            aria-invalid={touched && !!error}
            value={value || ''}
            name={fieldName || name}
            placeholder={placeholder}
            onFocus={onFocus}
            onChange={e =>
              onChange ? onChange(e.target.value) : setFieldValue(name, e.target.value)
            }
            readOnly={readonly}
            disabled={disabled}
            className={`input ${customClass} ${
              submitCount > 0 && error ? 'err-input' : ''
            } ${version === 'highlighted' ? 'highlighted' : ''}`}
          />
        </div>
      );
    return (
      <div className="input-wrapper">
        {icon}
        <input
          {...props}
          id={uid}
          type={type === 'password' ? (!hidePassword ? '' : type) : type}
          aria-invalid={touched && !!error}
          value={value || ''}
          {...(!label ? { 'aria-label': placeholder } : {})}
          name={fieldName || name}
          placeholder={placeholder}
          onFocus={onFocus}
          onChange={e =>
            onChange ? onChange(e.target.value) : setFieldValue(name, e.target.value)
          }
          readOnly={readonly}
          disabled={disabled}
          className={`input ${customClass} ${
            submitCount > 0 && error ? 'err-input' : ''
          } ${version === 'highlighted' ? 'highlighted' : ''} ${
            icon ? ' input-group-with-icon' : ''
          } ${type === 'password' ? 'psw-input' : ''}`}
        />
        {type === 'password' ? renderPswIcon() : null}
      </div>
    );
  };

  return (
    <div>
      {label && <InputLabel required={required} aria-label={label ?? placeholder} htmlFor={uid} >{label}</InputLabel>}
      {renderControlElement()}
      {showValidationMessage && submitCount > 0 && error ? (
        <ValidationMessage
          optionalParams={optionalParams}
          errors={error}
          name={name}
          text={type === 'password' ? error['password'] : undefined}
          maxPswLength={maxPswLength}
          fieldName={fieldName || label}
        />
      ) : null}
    </div>
  );
};

export default StyledInput;